import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const MAX_ITEMS = 10;
const DEFAULT_LABEL = "Editor's Choice";

export default function TickerPanel() {
  const [label, setLabel] = useState(DEFAULT_LABEL);
  const [items, setItems] = useState([{ text: "", url: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const d = r.data || {};
      setLabel(d.ticker_label || DEFAULT_LABEL);
      const it = Array.isArray(d.ticker_items) ? d.ticker_items : [];
      setItems(it.length ? it.map((x) => ({ text: x.text || "", url: x.url || "", alt: x.alt || "" })) : [{ text: "", url: "", alt: "" }]);
    });
  }, []);

  const updateItem = (i, key, val) => {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  };
  const addItem = () => {
    setItems((prev) => (prev.length < MAX_ITEMS ? [...prev, { text: "", url: "", alt: "" }] : prev));
  };
  const removeItem = (i) => {
    setItems((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));
  };

  const save = async (e) => {
    e.preventDefault();
    const cleaned = items
      .map((it) => ({ text: (it.text || "").trim(), url: (it.url || "").trim(), alt: (it.alt || "").trim() || undefined }))
      .filter((it) => it.text && it.url);
    if (cleaned.length === 0) {
      toast.error("Add at least one ticker item with a title and a URL.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        ticker_label: label.trim() || DEFAULT_LABEL,
        ticker_items: cleaned,
      });
      toast.success("Ticker updated. Refresh the front page to see it.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="border border-black p-6 space-y-6" data-testid="ticker-panel">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-700">Front-page ticker</div>
        <p className="text-sm text-neutral-700 mt-2">
          Add up to {MAX_ITEMS} short items. Each needs a title and a URL (internal like <code className="mono text-xs">/polls</code> or external like <code className="mono text-xs">https://…</code>). The label below is inserted between every 8 items on the ticker.
        </p>
      </div>

      <div>
        <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Ticker label (recurring header)</label>
        <input
          data-testid="ticker-label-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={40}
          className="mt-2 w-full border-b border-neutral-400 focus:border-black bg-transparent py-2 focus:outline-none serif text-xl"
          placeholder="Editor's Choice"
        />
      </div>

      <div className="space-y-3">
        <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Items ({items.length}/{MAX_ITEMS})</label>
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,auto] gap-2 sm:items-center border border-neutral-200 p-3">
            <input
              data-testid={`ticker-item-text-${i}`}
              value={it.text}
              onChange={(e) => updateItem(i, "text", e.target.value)}
              maxLength={120}
              placeholder="Title"
              className="border border-neutral-300 focus:border-black bg-transparent px-3 py-2 focus:outline-none"
            />
            <input
              data-testid={`ticker-item-url-${i}`}
              value={it.url}
              onChange={(e) => updateItem(i, "url", e.target.value)}
              maxLength={500}
              placeholder="/route  or  https://example.com"
              className="border border-neutral-300 focus:border-black bg-transparent px-3 py-2 focus:outline-none mono text-xs"
            />
            <button
              type="button"
              data-testid={`ticker-item-remove-${i}`}
              onClick={() => removeItem(i)}
              disabled={items.length === 1}
              className="mono text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed px-2"
            >
              Remove
            </button>
            <input
              data-testid={`ticker-item-alt-${i}`}
              value={it.alt || ""}
              onChange={(e) => updateItem(i, "alt", e.target.value)}
              maxLength={200}
              placeholder="Tooltip / alt text shown on hover (optional)"
              className="sm:col-span-3 border border-neutral-300 focus:border-black bg-transparent px-3 py-2 focus:outline-none text-sm"
            />
          </div>
        ))}
        {items.length < MAX_ITEMS && (
          <button
            type="button"
            data-testid="ticker-item-add"
            onClick={addItem}
            className="mono text-xs uppercase tracking-[0.2em] border border-neutral-300 px-3 py-1 hover:border-black"
          >
            + Add item
          </button>
        )}
      </div>

      <div className="flex justify-end border-t border-neutral-200 pt-4">
        <button
          type="submit"
          data-testid="ticker-save-button"
          disabled={saving}
          className="bg-black text-white px-6 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-amber-700 disabled:opacity-40"
        >
          {saving ? "Updating…" : "Update ticker"}
        </button>
      </div>
    </form>
  );
}
